import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface TierFeature {
  feature_key: string;
  free_access: boolean;
  basic_access: boolean;
  pro_access: boolean;
}

interface TierPricing {
  tier_key: string;
  tier_name: string;
  price_amount: number;
  price_label: string;
}

interface TierConfigContextType {
  features: TierFeature[];
  pricing: TierPricing[];
  loading: boolean;
  checkFeatureAccess: (featureKey: string, tier: 'free' | 'core' | 'pro' | 'plus' | 'basic') => boolean;
  getPricing: (tierKey: string) => TierPricing | undefined;
}

const TierConfigContext = createContext<TierConfigContextType>({
  features: [],
  pricing: [],
  loading: true,
  checkFeatureAccess: () => false,
  getPricing: () => undefined,
});

export function TierConfigProvider({ children }: { children: ReactNode }) {
  const [features, setFeatures] = useState<TierFeature[]>([]);
  const [pricing, setPricing] = useState<TierPricing[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const [featuresRes, pricingRes] = await Promise.all([
          supabase
            .from('tier_features')
            .select('feature_key, free_access, basic_access, pro_access'),
          supabase
            .from('tier_pricing_public')
            .select('tier_key, tier_name, price_amount, price_label')
            .order('display_order', { ascending: true }),
        ]);

        if (featuresRes.error) throw featuresRes.error;
        if (pricingRes.error) throw pricingRes.error;
        
        setFeatures(featuresRes.data || []);
        setPricing(pricingRes.data || []);
      } catch (err) {
        console.error('Error fetching tier config:', err);
        // Fall back to defaults on error
        setFeatures([]);
        setPricing([
          { tier_key: 'free', tier_name: 'Free', price_amount: 0, price_label: '$0/month' },
          { tier_key: 'basic', tier_name: 'Basic', price_amount: 29, price_label: '$29/month' },
          { tier_key: 'pro', tier_name: 'Pro', price_amount: 79, price_label: '$79/month' },
        ]);
      } finally {
        setLoading(false);
      }
    };

    fetchConfig();
  }, []);

  const checkFeatureAccess = (featureKey: string, tier: 'free' | 'core' | 'pro' | 'plus' | 'basic'): boolean => {
    // Normalize legacy 'basic' → 'core'
    const normalized = tier === 'basic' ? 'core' : tier;
    const feature = features.find(f => f.feature_key === featureKey);

    // If feature not found in database, default to allowing access for paid tiers
    if (!feature) {
      return normalized !== 'free';
    }

    // tier_features table currently has free_access / basic_access / pro_access columns.
    // Map: free → free_access; core → basic_access (legacy column); pro & plus → pro_access.
    switch (normalized) {
      case 'free':
        return feature.free_access;
      case 'core':
        return feature.basic_access;
      case 'pro':
      case 'plus':
        return feature.pro_access;
      default:
        return false;
    }
  };

  const getPricing = (tierKey: string): TierPricing | undefined => {
    return pricing.find(p => p.tier_key === tierKey);
  };

  return (
    <TierConfigContext.Provider value={{ features, pricing, loading, checkFeatureAccess, getPricing }}>
      {children}
    </TierConfigContext.Provider>
  );
}

export function useTierFeatureAccess() {
  return useContext(TierConfigContext);
}
